import styled, { keyframes } from "styled-components";
import { Flex, Text } from "rebass/styled-components";

const livePulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 68, 68, 0.55);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(255, 68, 68, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 68, 68, 0);
  }
`;

export const TakeoverBand = styled.div`
  position: relative;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breaks.ultrawide}) {
    margin-bottom: 28px;
  }
`;

export const Hero = styled.div<{ image: string }>`
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  gap: 28px;
  min-height: 320px;
  padding: 30px 32px;
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  background-color: ${({ theme }) => theme.colors.navy500};
  background-image: ${({ image }) => (image ? `url(${image})` : "none")};
  background-position: center 38%;
  background-repeat: no-repeat;
  background-size: cover;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    background: linear-gradient(
        90deg,
        rgba(10 16 20 / 0.88) 0%,
        rgba(10 16 20 / 0.55) 32%,
        rgba(10 16 20 / 0.15) 60%,
        transparent 82%
      ),
      linear-gradient(0deg, rgba(12 19 24 / 0.4) 0%, transparent 38%);
  }

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    flex-direction: column;
    align-items: stretch;
    min-height: auto;
    padding: 20px;
  }
`;

export const ContentColumn = styled(Flex)`
  position: relative;
  z-index: 2;
  flex: 1 1 auto;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  text-align: left;
  gap: 14px;
  min-width: 300px;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    width: 100%;
    min-width: 0;
  }
`;

export const ClipsMiddle = styled.div`
  position: relative;
  z-index: 2;
  flex: 0 1 920px;
  min-width: 0;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding-bottom: 6px;

  @media (max-width: ${({ theme }) => theme.breaks.wide}) {
    display: none;
  }
`;

export const TakeoverTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 40px;
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.02em;
  text-shadow: 0 2px 14px rgba(0, 0, 0, 0.6);

  @media (max-width: ${({ theme }) => theme.breaks.wide}) {
    font-size: 32px;
  }

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    font-size: 28px;
  }

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    font-size: 24px;
  }
`;

export const PrizeRow = styled(Flex)`
  align-items: center;
  gap: 10px;
`;

export const PrizeLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.goldStar};
`;

export const PrizeText = styled.div`
  font-size: 22px;
  font-weight: 800;
  line-height: 1.15;
  color: ${({ theme }) => theme.colors.ice};
  text-shadow: 0 1px 10px rgba(0, 0, 0, 0.6);
`;

export const LiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 7px;
  background: rgba(255, 68, 68, 0.14);
  border: 1px solid rgba(255, 68, 68, 0.35);
  border-radius: 999px;
  padding: 4px 11px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.heartRed ?? "#ff4444"};
`;

export const LiveDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.heartRed ?? "#ff4444"};
  flex-shrink: 0;
  animation: ${livePulse} 1.8s ease-out infinite;
`;

export const StatRow = styled(Flex)`
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 18px;
`;

export const StatPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.chalk};
  white-space: nowrap;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.6);

  svg {
    color: ${({ theme }) => theme.colors.envy};
    flex-shrink: 0;
  }
`;

export const StatNum = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-weight: 800;
`;

export const CountdownBlock = styled(Flex)`
  align-items: center;
`;

export const CountdownLabel = styled.span<{ accent: string }>`
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: ${({ accent }) => accent};
  margin-right: 8px;
`;

export const EnterButton = styled.button<{ upcoming?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  cursor: pointer;
  border-radius: 10px;
  padding: 13px 24px;
  font-size: 15px;
  font-weight: 800;
  margin-top: 4px;
  color: ${({ theme }) => theme.colors.midnight};
  background: ${({ upcoming, theme }) =>
    upcoming ? theme.colors.caution : theme.colors.envy};
  transition:
    transform 0.15s ease,
    filter 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.06);
  }
`;

export const ColumnLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 10px;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.6);
`;

export const ClipsRail = styled.div`
  display: flex;
  align-items: stretch;
  gap: 14px;
  width: 100%;
  overflow-x: auto;
  padding: 0 16px 6px 0;
  scrollbar-width: none;
  -webkit-mask-image: linear-gradient(
    90deg,
    #000 0%,
    #000 86%,
    transparent 100%
  );
  mask-image: linear-gradient(90deg, #000 0%, #000 86%, transparent 100%);

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const HeroPanel = styled.div`
  position: relative;
  z-index: 2;
  flex: 0 0 380px;
  width: 380px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: rgba(12, 19, 24, 0.55);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(247, 252, 253, 0.1);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35);

  @media (max-width: ${({ theme }) => theme.breaks.standard}) {
    flex-basis: 330px;
    width: 330px;
  }

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    width: 100%;
    flex-basis: auto;
  }
`;

export const TabsWrap = styled.div`
  padding: 10px 16px 0;
`;

export const TabBody = styled.div`
  height: 286px;
  overflow-y: auto;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(143, 169, 175, 0.25);
    border-radius: 999px;
  }
`;
